import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as procService from "../../services/procService";
import { useModule } from "../Platform/ModuleGate";
import { hasModulePermission } from "../../constants/platformModules";
import { ChatPanel } from "./Chat";
import { DataTable, ProcBadge, act, amount, linkBtn, openSigned, orderKey } from "./procUi";
import { DetailGrid, LoadState, PageHeader, PrimaryButton, SecondaryButton, cardStyle, formatDate, formatDateTime, pageStyle, useLoad } from "../../components/ui";

// One order (buyer side): the supplier, the items, what happened, and the purchase order in Inventory.
export default function OrderDetail() {
  const { id } = useParams();
  const { access } = useModule();
  const canWrite = hasModulePermission(access, "PROC", "PROC_ADMIN", "PROC_BUYER");
  const [chatting, setChatting] = useState(false);
  const { data: o, loading, error, reload } = useLoad(() => procService.getOrder(id), [id]);
  const complete = () => act(() => procService.completeOrder(id), "Order completed").then((r) => r && reload());

  return (
    <div style={pageStyle}>
      <PageHeader
        title={o ? `Order ${o.order_number}` : "Order"}
        subtitle={o ? `${o.company_name} · ${o.rfq_number}: ${o.rfq_title}` : ""}
        actions={o ? <span className="d-flex gap-2 align-items-center flex-wrap"><ProcBadge status={orderKey(o.status)} label={o.status_label} />{canWrite && o.can_complete ? <PrimaryButton onClick={complete}>Complete order</PrimaryButton> : null}<SecondaryButton onClick={() => setChatting(true)}>Message the supplier</SecondaryButton></span> : null}
      />
      <LoadState loading={loading} error={error} onRetry={reload}>
        {o ? (
          <>
            <div style={{ ...cardStyle, padding: 16 }} className="mb-4">
              <DetailGrid
                columns={3}
                items={[
                  ["Supplier", o.company_name],
                  ["Total", `${o.currency} ${amount(o.total)}`],
                  ["Delivery date", o.delivery_date ? formatDate(o.delivery_date) : null],
                  ["Payment terms", o.payment_terms],
                  ["Delivery location", o.delivery_location],
                  ["Award", o.award_number],
                  ["Purchase order", o.po_status ? `${o.order_number} (${o.po_status.toLowerCase().replace(/_/g, " ")})` : o.order_number],
                  ["Accepted", o.accepted_at ? formatDateTime(o.accepted_at) : null],
                ]}
              />
              {o.status === 4 && o.reject_reason ? <div className="mt-3" style={{ fontSize: 13, color: "#991B1B" }}>Rejected: {o.reject_reason}</div> : null}
              <div className="mt-3" style={{ fontSize: 13 }}>
                Goods are received against this purchase order under <Link to="/inventory/purchase-orders">Inventory › Purchase orders</Link>.
              </div>
            </div>
            <h6>Items</h6>
            <DataTable
              minWidth={560}
              rowKey={(l) => l.id}
              rows={o.lines}
              columns={[
                { label: "Item", render: (l) => `${l.line_no}. ${l.item}` },
                { label: "Quantity", render: (l) => `${l.quantity} ${l.uom || ""}${l.split_pct < 100 ? ` (${l.split_pct}% of the item)` : ""}` },
                { label: "Unit price", render: (l) => amount(l.unit_price) },
                { label: "Amount", render: (l) => amount(l.amount) },
              ]}
              footer={<tr><th colSpan={3} style={{ padding: "10px 12px" }}>Total ({o.currency})</th><th style={{ padding: "10px 12px" }}>{amount(o.total)}</th></tr>}
            />
            <h6 className="mt-4">Invoices</h6>
            <DataTable
              minWidth={560}
              rowKey={(i) => i.invoice_number}
              rows={o.invoices}
              empty={<div style={{ fontSize: 13, color: "#6B7280" }}>The supplier has not sent an invoice yet.</div>}
              columns={[
                { label: "Invoice", render: (i) => <button type="button" style={linkBtn} onClick={() => openSigned(() => procService.getOrderInvoiceUrl(i.id), toast)}>{i.invoice_number}</button> },
                { label: "Date", render: (i) => formatDate(i.invoice_date) },
                { label: "Amount", render: (i) => `${i.currency} ${amount(i.amount)}`, style: { whiteSpace: "nowrap" } },
                { label: "Supplier bill (Payables)", render: (i) => (i.bill_number ? `Draft ${i.bill_number}` : i.bill_note ? <span style={{ color: "#991B1B" }}>Not created: {i.bill_note}</span> : <span style={{ color: "#6B7280" }}>Made once the goods are received</span>) },
              ]}
            />
            <h6 className="mt-4">History</h6>
            <DataTable
              minWidth={440}
              rowKey={(h) => h.id}
              rows={o.history}
              columns={[
                { label: "When", render: (h) => formatDateTime(h.created_at), style: { whiteSpace: "nowrap" } },
                { label: "What", render: (h) => h.note || h.status },
                { label: "By", render: (h) => ({ SYSTEM: "System", BUYER: "Buyer", SUPPLIER: o.company_name }[h.actor_role]) },
              ]}
            />
            {chatting ? <ChatPanel service={procService} refQuery={{ supplier_id: o.supplier_id, rfq_id: o.rfq_id }} title={o.company_name} subtitle={`${o.order_number} · ${o.rfq_number}`} canWrite={canWrite} onClose={() => setChatting(false)} /> : null}
          </>
        ) : null}
      </LoadState>
    </div>
  );
}
